import { errorMessage } from "@streamoid/errors";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { notify } from "../../utils/utils";
import { createOrganization, getOrganizations } from "../../utils/organizations";
import "./style.css";
import { BARE_DOMAIN_MESSAGE, isBareDomain } from "./domain";

export default function Organizations() {
  const navigate = useNavigate();

  const [organizations, setOrganizations] = useState<any[]>([]);
  const [searchStr, setSearchStr] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [newOrgName, setNewOrgName] = useState("");
  // The company domain (e.g. abfrl.com): colleagues on it are matched to the
  // org at sign-up, and its admins can create more orgs for it. Without one
  // the org is invite-only, so it can only be private.
  const [newOrgDomain, setNewOrgDomain] = useState("");
  const [newOrgType, setNewOrgType] = useState("shared");
  const [isCreating, setIsCreating] = useState(false);

  const fetchOrganizations = () => {
    setIsLoading(true);
    getOrganizations()
      .then((res) => {
        setOrganizations(res?.data || []);
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchOrganizations();
  }, []);

  const handleCreate = () => {
    const name = newOrgName.trim();
    if (!name) {
      notify("Organization name is required", "error");
      return;
    }

    const domain = newOrgDomain.trim().toLowerCase();
    if (domain && !isBareDomain(domain)) {
      notify(BARE_DOMAIN_MESSAGE, "error");
      return;
    }
    setIsCreating(true);
    createOrganization(domain ? { name, domain, type: newOrgType } : { name })
      .then(() => {
        notify("Organization created successfully");
        setNewOrgName("");
        setNewOrgDomain("");
        setNewOrgType("shared");
        fetchOrganizations();
      })
      .catch((err) => {
        console.log(err);
        notify(errorMessage(err), "error");
      })
      .finally(() => {
        setIsCreating(false);
      });
  };

  const filteredOrganizations = organizations.filter((org) =>
    org.name?.toLowerCase().includes(searchStr.trim().toLowerCase())
  );

  return (
    <div className="organizations-page">
      <div className="dashboard-settings-section">
        <div className="section-heading">Organizations</div>

        <div className="settings-container organizations-create-panel">
          <div className="version-header">
            <div className="version-title">Create organization</div>
            <div className="version-subtitle">
              Spin up a new organization, then add existing workspaces to it
              from the detail page. Give it the company's email domain so
              colleagues are matched to it and its admins can create more
              organizations; leave the domain empty for an invite-only one.
            </div>
          </div>
          <div className="organization-fields">
            <label className="organization-field">
              <span>Name</span>
              <input
                className="version-input"
                value={newOrgName}
                placeholder="Organization name"
                onChange={(e) => setNewOrgName(e.target.value)}
                disabled={isCreating}
              />
            </label>
            <label className="organization-field">
              <span>Company domain (optional)</span>
              <input
                className="version-input"
                value={newOrgDomain}
                placeholder="e.g. acme.com"
                onChange={(e) => setNewOrgDomain(e.target.value)}
                disabled={isCreating}
              />
            </label>
            <label className="organization-field">
              <span>Who can join</span>
              <select
                className="version-input"
                value={newOrgDomain.trim() ? newOrgType : "private"}
                onChange={(e) => setNewOrgType(e.target.value)}
                disabled={isCreating || !newOrgDomain.trim()}
              >
                <option value="shared">Shared</option>
                <option value="private">Private</option>
              </select>
            </label>
          </div>
          <div className="version-controls organization-actions">
            <button
              className="version-save-btn"
              onClick={handleCreate}
              disabled={isCreating}
            >
              {isCreating ? "Creating..." : "Create"}
            </button>
          </div>
        </div>

        <div className="settings-container organizations-list-panel">
          <div className="version-header">
            <div className="version-title">All organizations</div>
            <div className="version-subtitle">
              {organizations.length} total
            </div>
          </div>

          <input
            className="version-input organizations-search-input"
            value={searchStr}
            placeholder="Search organizations..."
            onChange={(e) => setSearchStr(e.target.value)}
          />

          {isLoading ? (
            <div className="version-subtitle">Loading...</div>
          ) : filteredOrganizations.length === 0 ? (
            <div className="version-subtitle">No organizations found.</div>
          ) : (
            <div className="organizations-table-scroll">
              <table className="organizations-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Domain</th>
                    <th>Type</th>
                    <th>Workspaces</th>
                    <th>Members</th>
                    <th>Status</th>
                    <th>Added by</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredOrganizations.map((org) => (
                    <tr
                      key={org._id}
                      className="organizations-table-row"
                      onClick={() => navigate(`/organizations/${org._id}`)}
                    >
                      <td>
                        <div className="organizations-name-cell">
                          {org.imageUrl ? (
                            <img
                              src={org.imageUrl}
                              alt={org.name}
                              className="organizations-avatar"
                            />
                          ) : (
                            <div className="organizations-avatar organizations-avatar-placeholder">
                              {org.name?.substring(0, 1)}
                            </div>
                          )}
                          {org.name}
                        </div>
                      </td>
                      <td>{org.domain || "-"}</td>
                      <td>{org.domain ? org.type || "shared" : "private"}</td>
                      <td>{org.workspaceCount ?? 0}</td>
                      <td>{org.memberCount ?? 0}</td>
                      <td>
                        <span
                          className={`organizations-status-badge organizations-status-${
                            org.status || "active"
                          }`}
                        >
                          {org.status || "active"}
                        </span>
                      </td>
                      <td>{org.addedBy?.email || "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
